import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { api } from '@/lib/api';
import { useInvalidateCredits } from '@/features/dashboard/hooks/useCredits';
import { SCREENING_BATCH_SIZE, getBucketCategory } from '@/features/evaluation/types';
import type { BucketCategory, ScreeningProgress, ScreeningVerdict } from '@/features/evaluation/types';
import { toast } from 'sonner';

const EVALUATIONS_KEY = 'evaluations';
const MAX_CONCURRENT = 10;

/** Split array into chunks of given size */
function chunk<T>(arr: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
}

/** Sources covered by 1 credit (x10 margin on GPT-4.1-mini ~$0.001/source) */
const SOURCES_PER_CREDIT = 30;

/** Estimate credits needed for screening */
export function estimateScreeningCredits(sourceCount: number): number {
  return Math.ceil(sourceCount / SOURCES_PER_CREDIT);
}

/** Call the screen-sources API for a single batch */
async function callScreenBatch(
  batch: string[],
  _token: string,
  criteriaIds: string[],
): Promise<ScreeningVerdict[]> {
  const data = await api.screenSources({
    source_ids: batch,
    criteria_ids: criteriaIds,
  });
  return data.results.map((r) => ({
    ...r,
    verdict: r.verdict as BucketCategory,
  }));
}

/** Write AI scores to review_sources and review_decisions */
async function writeAIResults(
  reviewId: string,
  userId: string,
  results: ScreeningVerdict[],
): Promise<void> {
  // Upsert screening_priority into review_sources
  for (const r of results) {
    await supabase
      .from('review_sources')
      .update({ screening_priority: r.score / 100 })
      .eq('review_id', reviewId)
      .eq('source_id', r.source_id);
  }

  // Upsert AI decisions into review_decisions
  const decisionRows = results.map((r) => ({
    review_id: reviewId,
    source_id: r.source_id,
    reviewer_id: userId,
    decision: r.verdict,
    reason: r.reason,
    phase: 'screening' as const,
    decision_source: 'ai' as const,
    reviewed_at: new Date().toISOString(),
  }));

  if (decisionRows.length > 0) {
    await supabase
      .from('review_decisions')
      .upsert(decisionRows, {
        onConflict: 'review_id,source_id,reviewer_id,phase',
      });
  }
}

/** Auto-bucket screening results into Include/Maybe/Exclude folders */
async function autoBucketResults(
  userId: string,
  results: ScreeningVerdict[],
): Promise<{ include: number; maybe: number; exclude: number }> {
  const bucketNames = ['Include', 'Maybe', 'Exclude'] as const;
  const folderIds: Record<string, string> = {};

  // Create or find result folders
  for (const name of bucketNames) {
    const { data: existing } = await supabase
      .from('folders')
      .select('id')
      .eq('user_id', userId)
      .eq('name', `Results: ${name}`)
      .eq('folder_type', 'result')
      .maybeSingle();

    if (existing) {
      folderIds[name.toLowerCase()] = existing.id;
    } else {
      const color = name === 'Include' ? 'emerald' : name === 'Maybe' ? 'amber' : 'rose';
      const { data: created } = await supabase
        .from('folders')
        .insert({ user_id: userId, name: `Results: ${name}`, folder_type: 'result', color })
        .select('id')
        .single();
      if (created) folderIds[name.toLowerCase()] = created.id;
    }
  }

  // Assign each source to its bucket folder
  const counts = { include: 0, maybe: 0, exclude: 0 };
  for (const r of results) {
    const category = getBucketCategory(r.score);
    const folderId = folderIds[category];
    if (folderId) {
      await supabase
        .from('folder_sources')
        .upsert(
          { folder_id: folderId, source_id: r.source_id },
          { onConflict: 'folder_id,source_id' },
        );
    }
    counts[category]++;
  }

  return counts;
}

/** Run batch screening — 50 sources per API call, 10 batches in parallel */
export function useScreenSources() {
  const queryClient = useQueryClient();
  const invalidateCredits = useInvalidateCredits();

  return useMutation({
    mutationFn: async ({
      source_ids,
      criteria_ids,
      reviewId,
      onProgress,
    }: {
      source_ids: string[];
      criteria_ids: string[];
      reviewId?: string;
      onProgress?: (progress: ScreeningProgress) => void;
    }) => {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      const userId = sessionData.session?.user?.id;
      if (!token || !userId) throw new Error('Not authenticated');

      const batches = chunk(source_ids, SCREENING_BATCH_SIZE);

      const progress: ScreeningProgress = {
        total_sources: source_ids.length,
        total_batches: batches.length,
        completed_batches: 0,
        completed_sources: 0,
        failed_batches: 0,
        results: [],
      };

      // Process batches in waves of MAX_CONCURRENT
      for (let i = 0; i < batches.length; i += MAX_CONCURRENT) {
        const wave = batches.slice(i, i + MAX_CONCURRENT);

        onProgress?.({ ...progress });

        const results = await Promise.allSettled(
          wave.map((batch) => callScreenBatch(batch, token, criteria_ids)),
        );

        const waveResults: ScreeningVerdict[] = [];

        for (let j = 0; j < results.length; j++) {
          const result = results[j]!;
          const batch = wave[j]!;
          if (result.status === 'fulfilled') {
            progress.completed_batches += 1;
            progress.completed_sources += batch.length;
            progress.results.push(...result.value);
            waveResults.push(...result.value);
          } else {
            progress.failed_batches += 1;
            progress.completed_sources += batch.length;
          }
        }

        // Write AI results to review_sources if reviewId provided
        if (reviewId && waveResults.length > 0) {
          await writeAIResults(reviewId, userId, waveResults);
        }

        onProgress?.({ ...progress });
      }

      return progress;
    },
    onSuccess: async (progress) => {
      // Auto-bucket results into Include/Maybe/Exclude folders
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const userId = sessionData.session?.user?.id;
        if (userId && progress.results.length > 0) {
          await autoBucketResults(userId, progress.results);
        }
      } catch {
        // Bucketing is non-critical — don't fail the screening
      }

      queryClient.invalidateQueries({ queryKey: [EVALUATIONS_KEY] });
      queryClient.invalidateQueries({ queryKey: ['review-sources'] });
      queryClient.invalidateQueries({ queryKey: ['review-decisions'] });
      queryClient.invalidateQueries({ queryKey: ['review-stats'] });
      queryClient.invalidateQueries({ queryKey: ['folders'] });
      queryClient.invalidateQueries({ queryKey: ['folder-sources'] });
      invalidateCredits();

      const include = progress.results.filter(
        (r) => r.verdict === 'include',
      ).length;
      const maybe = progress.results.filter(
        (r) => r.verdict === 'maybe',
      ).length;
      const exclude = progress.results.filter(
        (r) => r.verdict === 'exclude',
      ).length;

      toast.success(
        `Screening complete: ${include} include, ${maybe} maybe, ${exclude} exclude`,
      );
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}
